import { Observable } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import { map } from 'rxjs/operators';
import { NodeSettingsFormService } from '../../../connector/connector-form/services/node-settings-form.service';
import { ConnectorAuthTypeEnum } from '../../../connector/connector-form/interfaces/authType';
import ActivateTargetAPI from '../ActivateTargetAPI';
import { TARGET_TYPES, TargetType } from '../models/target-type';
import { TargetConfigHelperService } from './target-config-helper.service';


export class TargetSettingsService extends NodeSettingsFormService<TargetConfigHelperService, TargetType> {
  static deps = [
    TargetConfigHelperService
  ];

  constructor(
    protected _targetConfigHelperService: TargetConfigHelperService
  ) {
    super(_targetConfigHelperService);
  }

  private static _buildAuthParams(targetTypeIdentifier: string, data) {
    const authData: any = {};

    if (TARGET_TYPES[targetTypeIdentifier].requireAuth) {
      authData.oauth_token_id = data.oauthTokenId;
    }

    if (TARGET_TYPES[targetTypeIdentifier].requireServiceAuth) {
      if (data.authType === ConnectorAuthTypeEnum.OAUTH) {
        authData.oauth_token_id = data.oauthTokenId;
      }

      if (data.authType === ConnectorAuthTypeEnum.SERVICE) {
        authData.security_service_account_id = data.serviceAccountId;
        delete authData.oauth_token_id;
      }

      authData.account_type = data.authType;
    }

    return authData;
  }

  getConnectionParams(targetType: TargetType, formData, isUpdateMode = false) {
    const configHelperInstance = this._getConfigHelperInstance(targetType.name);

    return {
      ...TargetSettingsService._buildAuthParams(targetType.name, formData),
      ...configHelperInstance.buildConnectionParams(formData, isUpdateMode)
    };
  }

  getExistingTargets() {
    return this._getExistingNodes$();
  }

  protected _getConfigFromRawData(targetType: TargetType, rawData: any, metaData: any, isUpdateMode = false) {
    const configHelperInstance = this._getConfigHelperInstance(targetType.name);

    const config: any = {
      ...(configHelperInstance ? configHelperInstance.getConfigFromRawData(rawData, metaData, isUpdateMode) : {})
    };

    const requireAuth = targetType.requireAuth || targetType.requireServiceAuth;

    if (requireAuth) {
      // config.authorisedAccount = rawData.authenticated_user;
      config.authType = ConnectorAuthTypeEnum[rawData.account_type];
    }

    if ('oauth_token_id' in rawData) {
      config.oauthTokenId = rawData.oauth_token_id;
    }

    if ('security_service_account_id' in rawData) {
      config.serviceAccountId = rawData.security_service_account_id;
    }

    return config;
  }

  protected _getConfigFromInitialData(targetType: TargetType, rawData: any, metaData: any, isUpdateMode = false) {
    const configHelperInstance = this._getConfigHelperInstance(targetType.name);

    const config: any = {
      ...(configHelperInstance ? configHelperInstance.getConfigFromInitialData(rawData, metaData, isUpdateMode) : {})
    };

    const requireAuth = targetType.requireAuth || targetType.requireServiceAuth;

    if (requireAuth) {
      // config.authorisedAccount = rawData.authenticated_user;
      config.authType = ConnectorAuthTypeEnum[rawData.account_type];
    }

    if ('oauth_token_id' in rawData) {
      config.oauthTokenId = rawData.oauth_token_id;
    }

    if ('security_service_account_id' in rawData) {
      config.serviceAccountId = rawData.security_service_account_id;
    }

    return config;
  }

  protected _getDefaultConfig(targetType: TargetType, metaData: any): any {
    const configHelperInstance = this._getConfigHelperInstance(targetType.name);

    return {
      ...(configHelperInstance ? configHelperInstance.getDefaultConfig(metaData) : {})
    };
  }

  protected _getExistingNodes$?(targetType?: TargetType): Observable<any[]> {
    return fromPromise(ActivateTargetAPI.getTargets()).pipe(
      map((res) => res?.data || [])
    );
  }
}
